import { fixDateInstance } from 'utils'
import { IAddress } from '../address/models'
import { mapOrderStatusFromDto } from '../orders/helpers'
import { OrderStatus } from '../orders/models'
import {
	CaseCategoryDtoType,
	CaseMaterialStatusDtoType,
	ICaseDetailsAddressDto,
	ICaseDetailsRequestParamsDto,
	ICaseFiltersDto,
	IDatahubCaseDto,
} from './dtos'
import {
	CaseCategory,
	CaseMaterialStatus,
	IDatahubCase,
	ICaseDetailsRequestParams,
	ICaseFilters,
	IGetCasesListParams,
	IUpdateCaseAddressParams,
} from './models'

const CATEGORY_MAP: Record<CaseCategoryDtoType, CaseCategory> = {
	CASE: CaseCategory.Case,
	LOCATION: CaseCategory.Location,
	PORTRAIT: CaseCategory.Portrait,
	GROUP: CaseCategory.Group,
	INTRO_OUTRO: CaseCategory.IntroOutro,
	SHOP: CaseCategory.Shop,
	Matterport: CaseCategory.Matterport,
	BEFOREAFTER: CaseCategory.BeforeAfter,
}

const MATERIAL_STATUS_MAP: Record<CaseMaterialStatusDtoType, CaseMaterialStatus> = {
	ACTIVE: CaseMaterialStatus.ACTIVE,
	ARCHIVING: CaseMaterialStatus.ARCHIVING,
	RESTORING: CaseMaterialStatus.RESTORING,
	ARCHIVED: CaseMaterialStatus.ARCHIVED,
}

export function mapCaseCategoryFromDto(dtoCategory: CaseCategoryDtoType): CaseCategory {
	return CATEGORY_MAP[dtoCategory]
}

export function mapMaterialStatusFromDto(dtoStatus: CaseMaterialStatusDtoType): CaseMaterialStatus {
	return MATERIAL_STATUS_MAP[dtoStatus]
}

export function mapCaseCategoryToDto(category: CaseCategory): CaseCategoryDtoType {
	return Object.keys(CATEGORY_MAP).find(
		key => CATEGORY_MAP[key as CaseCategoryDtoType] === category
	) as CaseCategoryDtoType
}

export function mapFullAddressFromUpdateCaseAddress(dto: IUpdateCaseAddressParams): IDatahubCase['address'] {
	let output = ''

	if (dto.street) {
		output += `${dto.street} `
	}

	if (dto.buildingNumber) {
		output += `${dto.buildingNumber}, `
	}

	if (dto.buildingFloor) {
		output += `${dto.buildingFloor}, `
	}

	if (dto.buildingDoor) {
		output += `${dto.buildingDoor}, `
	}

	if (dto.zip) {
		output += `${dto.zip} `
	}

	if (dto.city) {
		output += `${dto.city}`
	}

	return output.trim()
}

export function mapFullAddressFromCaseDetailsAddress(dto: ICaseDetailsAddressDto): IDatahubCase['address'] {
	let output = ''

	if (dto.ReferenceAddressStreet) {
		output += `${dto.ReferenceAddressStreet} `
	}

	if (dto.ReferenceAddressHouseNumber) {
		output += `${dto.ReferenceAddressHouseNumber}, `
	}

	if (dto.ReferenceAddressFloor) {
		output += `${dto.ReferenceAddressFloor}, `
	}

	if (dto.ReferenceAddressDoor) {
		output += `${dto.ReferenceAddressDoor}, `
	}

	if (dto.ReferenceAddressState) {
		output += `${dto.ReferenceAddressState}, `
	}

	if (dto.ReferenceAddressCityArea) {
		output += `${dto.ReferenceAddressCityArea}, `
	}

	if (dto.ReferenceAddressZip) {
		output += `${dto.ReferenceAddressZip} `
	}

	if (dto.ReferenceAddressCity) {
		output += `${dto.ReferenceAddressCity}`
	}

	return output.trim()
}

export function mapAddressFromCaseDetailsAddressDto(dto: ICaseDetailsAddressDto): IAddress | undefined {
	const {
		ReferenceAddressStreet: street,
		ReferenceAddressHouseNumber: houseNumber,
		ReferenceAddressFloor: floor,
		ReferenceAddressDoor: appartmentNumber,
		ReferenceAddressZip: postNumber,
		ReferenceAddressCity: city,
		ReferenceAddressState: area,
	} = dto

	return street && houseNumber && postNumber && city
		? {
				id: '<internal>',
				street,
				houseNumber,
				floor,
				appartmentNumber,
				postNumber,
				city,
				area,
				full: mapFullAddressFromCaseDetailsAddress(dto),
		  }
		: undefined
}

export function mapDatahubCaseFromDto({
	id,
	reference,
	axCustomerId,
	modifiedDate,
	branchId,
	branchName,
	address,
	ordersInfo,
	materialInfo,
	contactPersons,
	propertyInfo,
}: IDatahubCaseDto): IDatahubCase {
	return {
		id,
		reference,
		updatedAt: modifiedDate ? new Date(fixDateInstance(modifiedDate)) : undefined,
		deliveryDate:
			ordersInfo?.latestOrderRelatedChangeTimestamp != null
				? new Date(fixDateInstance(ordersInfo?.latestOrderRelatedChangeTimestamp))
				: null,
		departmentId: branchId,
		departmentName: branchName,
		address: address?.addressFull?.trim() || '',
		addressObj: address && {
			full: address.addressFull?.trim(),
			street: address.street,
			houseNumber: address.buildingNumber,
			city: address.city,
			postNumber: address.zip,
			floor: address.buildingFloor,
			appartmentNumber: address.buildingDoor,
		},
		orderStatus: ordersInfo?.orderStatus as OrderStatus,
		hasMaterials: !!materialInfo?.hasMaterials,
		isArchived: materialInfo?.materialsState === 'ARCHIVED' || materialInfo?.materialsState === 'ARCHIVING',
		clientId: axCustomerId,
		materialStatus: materialInfo?.materialsState as CaseMaterialStatus,
		categories: materialInfo?.deliveredProductCategories as CaseCategory[],
		contactPersons: contactPersons
			?.sort((prev, next) => prev.priority - next.priority)
			.map(({ contactType, name, contactInfo }) => ({
				type: contactType,
				name,
				email: contactInfo?.email,
				phone: contactInfo?.phoneMobile,
				phoneAlt: contactInfo?.phoneWork || contactInfo?.phonePrivate,
			})) || [
			{
				type: '',
				name: '',
				phone: '',
				phoneAlt: '',
			},
		],
		property: {
			type: propertyInfo?.propertyType,
			livingArea: propertyInfo?.registeredArea,
			basementArea: propertyInfo?.basementArea,
			floors: propertyInfo?.storeys,
		},
		modifiedDate: modifiedDate ? new Date(fixDateInstance(modifiedDate)) : undefined,
	}
}

export function mapCaseFiltersFromDto({ types, branches, statuses }: ICaseFiltersDto): ICaseFilters {
	return {
		categories: types?.filter(type => !!type).map(mapCaseCategoryFromDto) || [],
		statuses: statuses?.filter(status => !!status).map(mapOrderStatusFromDto) || [],
		departments: branches?.filter(branch => !!branch).map(({ id, name }) => ({ id, name })) || [],
	}
}

export function mapCaseOrderByParamToDto(orderBy: IGetCasesListParams['orderBy']): string | undefined {
	return (
		orderBy &&
		{
			ModifiedDate: 'ModifiedDate',
			Reference: 'Reference',
			Address: 'Address',
			Score: 'Score',
		}[orderBy]
	)
}

export function mapCaseDetailsRequestParamsToDto({
	case: caseObj,
	address,
	contactPersons,
	property,
	branchId,
}: ICaseDetailsRequestParams): ICaseDetailsRequestParamsDto {
	return {
		branchId,
		reference: caseObj.reference,
		orderContacts: contactPersons?.length
			? contactPersons?.map(({ name, phone, email, type }) => ({
					name,
					phone,
					email,
					contactType: type,
			  }))
			: [],
		...(address && {
			address: {
				street: address.street,
				buildingNumber: address.houseNumber,
				buildingFloor: address.floor,
				buildingDoor: address.appartmentNumber,
				cityArea: address.area,
				city: address.city,
				zip: address.postNumber,
			},
		}),
		...(property?.type && {
			propertyInfo: {
				propertyType: property.type ?? '',
				basementArea: property.basementArea ?? 0,
				registeredArea: property.livingArea ?? 0,
				totalStoreys: property.floors ?? 0,
			},
		}),
	}
}
